<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :key="tableKey"
            :columns="tableColumns"
            :items="paginatedItems"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableMultiSelect="true"
            :enableDragDrop="false"
            @page-changed="handlePageChange"
            :perPage="itemsPerPage"
            @per-page-changed="handlePerPageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
        >
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-gap-2 ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>

                        <!-- Filter By Type -->
                        <FilterByType :label="$t('annonce.filter_type')" :options="filterOptions" @filter-selected="selectType" />
                    </div>

                    <!-- Create Button Dropdown -->
                    <div class="ppv2-relative" ref="createDropdown">
                        <BaseButton
                            variant="purple"
                            :custom-height="40"
                            :custom-font-size="14"
                            custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                            @click="toggleCreateDropdown"
                        >
                            <Plus :size="17" />
                            <span class="poppinsRegular">{{ $t('common.create') }}</span>
                            <ChevronDown :size="17" />
                        </BaseButton>

                        <!-- Dropdown Menu -->
                        <div
                            v-if="showCreateDropdown"
                            class="ppv2-absolute ppv2-right-0 ppv2-mt-2 ppv2-w-48 ppv2-bg-white ppv2-rounded-xl ppv2-shadow-xl ppv2-z-50 ppv2-overflow-hidden ppv2-border ppv2-border-gray-100"
                        >
                            <a
                                href="/back-office/pop-up/create?type=banner"
                                class="ppv2-block ppv2-px-4 ppv2-py-3 ppv2-text-gray-700 hover:ppv2-bg-purple-50 hover:ppv2-text-[#7D2DBD] ppv2-transition-colors poppinsRegular"
                                :style="{ fontSize: normalizeFontSize(14) }"
                            >
                                {{ $t('annonce.add_banner') }}
                            </a>
                            <a
                                href="/back-office/pop-up/create?type=popup"
                                class="ppv2-block ppv2-px-4 ppv2-py-3 ppv2-text-gray-700 hover:ppv2-bg-purple-50 hover:ppv2-text-[#7D2DBD] ppv2-transition-colors poppinsRegular"
                                :style="{ fontSize: normalizeFontSize(14) }"
                            >
                                {{ $t('annonce.add_popup') }}
                            </a>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Column: Title -->
            <template #column-title="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.title }}
                </span>
            </template>

            <!-- Column: Image -->
            <template #column-image="{ item }">
                <div class="ppv2-flex-shrink-0">
                    <img
                        v-if="item.image"
                        :src="item.image"
                        alt="Image"
                        class="ppv2-w-10 ppv2-h-10 ppv2-object-cover ppv2-rounded-lg"
                    />
                    <div v-else class="ppv2-flex ppv2-items-center ppv2-justify-center">
                        <span class="ppv2-text-gray-400 text-[10px]">{{ $t('annonce.no_img') }}</span>
                    </div>
                </div>
            </template>

            <!-- Column: Type -->
            <template #column-type="{ item }">
                <div
                    class="textGray ppv2-inline-flex ppv2-px-3 ppv2-rounded-xl ppv2-text-base md:ppv2-text-lg"
                    :class="{
                        'bgBanner ppv2-bg-opacity-30': item.type === 'banner',
                        'bgPopup ppv2-bg-opacity-30': item.type === 'popup',
                    }"
                    :style="{ fontSize: normalizeFontSize(14) }"
                >
                    {{ item.type === 'banner' ? $t('annonce.banner') : $t('annonce.popup') }}
                </div>
            </template>

            <!-- Column: Dates -->
            <template #column-date="{ item }">
                <div class="ppv2-flex ppv2-flex-col">
                    <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                        {{ formatDate(item.start_date) }} - {{ formatDate(item.end_date) }}
                    </span>
                </div>
            </template>

            <!-- Column: Description -->
            <template #column-description="{ item }">
                <span
                    class="ppv2-text-black poppinsRegular ppv2-line-clamp-1"
                    :style="{ fontSize: normalizeFontSize(14) }"
                    :title="item.description"
                >
                    {{ item.description || '-' }}
                </span>
            </template>

            <!-- Column: Visible (Is Visible) -->
            <template #column-is_visible="{ item }">
                <button
                    @click="toggleVisible(item)"
                    class="ppv2-min-w-[125px] ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-1 ppv2-transition-all"
                    :class="
                        item.is_visible
                            ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                            : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                    "
                >
                    <div
                        class="ppv2-w-9 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all"
                        :class="item.is_visible ? 'ppv2-bg-[#206A64] ppv2-justify-end' : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'"
                    >
                        <div class="ppv2-w-3.5 ppv2-h-3.5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                    </div>
                    <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                        {{ item.is_visible ? $t('annonce.visible') : $t('annonce.invisible') }}
                    </span>
                </button>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div
                    :style="{ fontSize: normalizeFontSize(14) }"
                    class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                >
                    <button
                        @click="openEditTab(item)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.edit')"
                    >
                        <SquarePen :size="18" />
                    </button>
                    <button
                        class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.delete')"
                        @click="deleteSingleItem(item.id)"
                    >
                        <Trash2 :size="18" />
                    </button>
                </div>
            </template>

            <!-- Bulk Actions Slot -->
            <template #bulk-actions="{ selectedCount }">
                <span :style="{ fontSize: normalizeFontSize(15) }" class="poppinsRegular ppv2-text-black">
                    {{ $t('customers.selectedItems', { count: selectedCount, plural: selectedCount > 1 ? 's' : '' }) }}
                </span>
                <span class="poppinsRegular ppv2-text-white ppv2-text-opacity-50" :style="{ fontSize: normalizeFontSize(15) }"
                    >|</span
                >
                <BaseButton
                    variant="outline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="14" />
                    <span>{{ $t('common.deleteSelection') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { Plus, Trash2, SquarePen, ChevronDown } from 'lucide-vue'
import normalizeSize from '../../../../utils/normalizeSize'
import Search from '../../Experience/components/Search.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import DataTable from '../../../Shared/DataTable.vue'
import FilterByType from '../../../Shared/FilterByType.vue'
import axios from 'axios'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../../utils/perPage'

export default {
    name: 'AnnonceTable',
    props: {
        userId: {
            type: [Number, String],
            default: null,
        },
        token: {
            type: String,
            default: '',
        },
    },
    components: {
        Plus,
        ChevronDown,
        SquarePen,
        Trash2,
        Search,
        BaseButton,
        CustomDialog,
        DataTable,
        FilterByType,
    },
    data() {
        return {
            searchQuery: '',
            selectedType: 'tout',
            currentPage: 1,
            itemsPerPage: getPerPage('annonces_items_per_page', 10),
            selectedItems: [],
            selectAll: false,
            isLoading: false,
            error: null,
            tableColumns: [
                { key: 'title', label: this.$t('annonce.columns.title'), sortable: true, type: 'text' },
                { key: 'type', label: this.$t('annonce.columns.type'), sortable: true, type: 'text' },
                { key: 'date', label: this.$t('annonce.columns.date'), sortable: true, type: 'text' },
                { key: 'image', label: 'Image', sortable: false, type: 'image' },
                {
                    key: 'description',
                    label: this.$t('annonce.columns.description'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'is_visible',
                    label: this.$t('annonce.columns.visibility'),
                    sortable: true,
                    type: 'boolean',
                },
                { key: 'actions', label: this.$t('annonce.columns.actions'), sortable: false, type: 'actions' },
            ],
            showDeletePopup: false,
            showCreateDropdown: false,
            deletePopupTitle: this.$t('annonce.delete_title'),
            deletePopupDescription: '',
            deleteAction: null,
            itemToDelete: null,
            items: {
                data: [],
                total: 0,
                last_page: 1,
            },
            tableKey: 0,
            orderBy: 'id',
            orderDirection: 'desc',
            filterOptions: [
                { value: 'tout', label: this.$t('annonce.all') },
                { value: 'banner', label: this.$t('annonce.banner') },
                { value: 'popup', label: this.$t('annonce.popup') },
            ],
        }
    },
    computed: {
        paginatedItems() {
            return this.items.data || []
        },
        totalPages() {
            return this.items.last_page || 1
        },
    },
    mounted() {
        this.fetchItems()
        document.addEventListener('click', this.closeCreateDropdown)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeCreateDropdown)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        toggleCreateDropdown() {
            this.showCreateDropdown = !this.showCreateDropdown
        },
        closeCreateDropdown(event) {
            if (this.$refs.createDropdown && !this.$refs.createDropdown.contains(event.target)) {
                this.showCreateDropdown = false
            }
        },
        formatDate(date) {
            if (!date) return '-'
            return new Date(date).toLocaleDateString('fr-FR')
        },
        async toggleVisible(item) {
            const newStatus = !item.is_visible
            const action = newStatus ? 'enable' : 'disable'
            try {
                await axios.post(
                    `/api/v3/annonce/${action}`,
                    { id: item.id },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    }
                )
                item.is_visible = newStatus
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchItems()
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('annonces_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchItems()
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.currentPage = 1
            this.fetchItems()
        },
        selectType(type) {
            this.selectedType = type
            this.currentPage = 1
            this.fetchItems()
        },
        handleSort(key) {
            if (this.orderBy === key) {
                this.orderDirection = this.orderDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.orderBy = key
                this.orderDirection = 'asc'
            }
            this.currentPage = 1
            this.fetchItems()
        },
        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
            this.selectAll = this.selectedItems.length === this.paginatedItems.length
        },
        toggleSelectAll() {
            const allIds = this.paginatedItems.map((item) => item.id)
            const allSelected = allIds.length > 0 && allIds.every((id) => this.selectedItems.includes(id))
            if (allSelected) {
                this.selectedItems = []
            } else {
                this.selectedItems = [...allIds]
            }
        },
        async deleteSelectedItems() {
            if (this.selectedItems.length === 0) return
            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('annonce.delete_description_multiple')
            this.showDeletePopup = true
        },
        deleteSingleItem(itemId) {
            this.deleteAction = 'single'
            this.itemToDelete = itemId
            this.deletePopupDescription = this.$t('annonce.delete_description_single')
            this.showDeletePopup = true
        },
        async confirmDelete() {
            try {
                let ids = []
                if (this.deleteAction === 'single') {
                    ids = [this.itemToDelete]
                } else {
                    ids = this.selectedItems
                }

                await axios.post(
                    `/api/v3/annonce/delete`,
                    { ids: ids },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    }
                )

                this.fetchItems()
                this.selectedItems = []
                this.selectAll = false
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Delete error:', error)
                alert(this.$t('annonce.delete_error'))
            }
            this.showDeletePopup = false
            this.deleteAction = null
            this.itemToDelete = null
        },
        openEditTab(item) {
            window.location.href = `/back-office/pop-up/${item.id}/edit?type=${item.type}`
        },
        async fetchItems() {
            this.isLoading = true
            this.error = null
            try {
                let url = `/api/v3/annonce?currentPage=${this.currentPage}&itemsPerPage=${this.itemsPerPage}&search=${this.searchQuery}&orderBy=${this.orderBy}&orderDirection=${this.orderDirection}&type=${this.selectedType}`
                const response = await axios.get(url, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })
                const data = response.data
                this.items = {
                    data: data.data || [],
                    total: data.total || 0,
                    last_page: data.last_page || 1,
                }
            } catch (err) {
                this.error = err.message || this.$t('annonce.fetch_error')
                console.error('API error:', err)
            } finally {
                this.isLoading = false
            }
        },
    },
    watch: {
        selectedItems: {
            handler(newSelection) {
                const currentPageIds = this.paginatedItems.map((item) => item.id)
                this.selectAll = currentPageIds.length > 0 && currentPageIds.every((id) => newSelection.includes(id))
            },
            deep: true,
        },
    },
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}

.bgBanner {
    background-color: rgba(0, 117, 255, var(--tw-bg-opacity));
}

.bgPopup {
    background-color: rgba(219, 168, 0, var(--tw-bg-opacity));
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}
</style>
